// src/pages/Dashboard/UsedTokenByDate.js
import React, { useState, useEffect } from "react";
import { useParams } from 'react-router-dom';
import axiosInstance from "../../utils/axiosInstance";
import { Box, Typography, CircularProgress, Alert, Button, ButtonGroup, Paper, Divider, useTheme } from "@mui/material";
import { Bar } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import { useChatbots } from "../../contexts/ChatbotContext";

Chart.register(...registerables);

export default function UsedTokensChart() {
    const { activeChatbot } = useChatbots();
    const theme = useTheme();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [chartData, setChartData] = useState(null);
    const [viewMode, setViewMode] = useState("bySession");
    const [processedData, setProcessedData] = useState(null);
    const { chatbotId: paramsChatbotId } = useParams();

    useEffect(() => {
        if (activeChatbot) fetchData();
        else setChartData(null);
    }, [activeChatbot]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { chatbot_id: paramsChatbotId };
            const response = await axiosInstance.get(`/get-used-token-by-date`, { params });
            processChartData(response.data.data);
        } catch {
            setError("Failed to fetch token data for the selected chatbot.");
        } finally {
            setLoading(false);
        }
    };

    const processChartData = (data) => {
        const bySessionData = data;
        const aggregatedDaily = {};
        const monthly = {};
        const yearly = {};

        data.forEach(({ created_date, input_token, output_token, total_token }) => {
            // daily
            if (!aggregatedDaily[created_date]) aggregatedDaily[created_date] = { input: 0, output: 0, total: 0 };
            aggregatedDaily[created_date].input += input_token;
            aggregatedDaily[created_date].output += output_token;
            aggregatedDaily[created_date].total += total_token;

            // monthly
            const [year, month] = created_date.split("-");
            const ym = `${year}-${month}`;
            if (!monthly[ym]) monthly[ym] = { input: 0, output: 0, total: 0 };
            monthly[ym].input += input_token;
            monthly[ym].output += output_token;
            monthly[ym].total += total_token;

            // yearly
            if (!yearly[year]) yearly[year] = { input: 0, output: 0, total: 0 };
            yearly[year].input += input_token;
            yearly[year].output += output_token;
            yearly[year].total += total_token;
        });

        setProcessedData({ bySession: bySessionData, aggregatedDaily, monthly, yearly });
        updateChartData(viewMode, { bySession: bySessionData, aggregatedDaily, monthly, yearly }[viewMode]);
    };

    const updateChartData = (mode, data) => {
        let labels, inputTokens, outputTokens, totalTokens;

        if (mode === "bySession") {
            labels = data.map((i) => i.created_date);
            inputTokens = data.map((i) => i.input_token);
            outputTokens = data.map((i) => i.output_token);
            totalTokens = data.map((i) => i.total_token);
        } else {
            const entries = Object.entries(data);
            labels = entries.map(([k]) => k);
            inputTokens = entries.map(([_, v]) => v.input);
            outputTokens = entries.map(([_, v]) => v.output);
            totalTokens = entries.map(([_, v]) => v.total);
        }

        setChartData({
            labels,
            datasets: [
                { label: "Input Tokens", data: inputTokens, backgroundColor: theme.palette.primary.light },
                { label: "Output Tokens", data: outputTokens, backgroundColor: theme.palette.primary.main },
                { label: "Total Tokens", data: totalTokens, backgroundColor: theme.palette.info.main },
            ],
        });
    };

    const handleViewChange = (mode) => {
        setViewMode(mode);
        if (processedData) updateChartData(mode, processedData[mode]);
    };

    const options = { plugins: { legend: { display: true } }, responsive: true, scales: { x: { stacked: true }, y: { stacked: true } } };

    return (
        <Paper sx={{ p: 3, bgcolor: "background.paper" }}>
            <Typography variant="h5" color="text.primary" gutterBottom>
                Used Tokens for {activeChatbot?.chatbot_name || '...'}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <ButtonGroup variant="contained" sx={{ mb: 2 }}>
                <Button onClick={() => handleViewChange("bySession")} color={viewMode === "bySession" ? "primary" : "inherit"}>By Session</Button>
                <Button onClick={() => handleViewChange("aggregatedDaily")} color={viewMode === "aggregatedDaily" ? "primary" : "inherit"}>Daily</Button>
                <Button onClick={() => handleViewChange("monthly")} color={viewMode === "monthly" ? "primary" : "inherit"}>Monthly</Button>
                <Button onClick={() => handleViewChange("yearly")} color={viewMode === "yearly" ? "primary" : "inherit"}>Yearly</Button>
            </ButtonGroup>

            {loading && <CircularProgress />}
            {error && <Alert severity="error">{error}</Alert>}
            {!loading && !error && !chartData && (
                <Typography color="text.primary">No token data available for this chatbot.</Typography>
            )}
            {chartData && (
                <Box sx={{ mt: 3 }}>
                    <Bar data={chartData} options={options} />
                </Box>
            )}
        </Paper>
    );
}